/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import {
    AxisOptions, ChartOptions, IndividualSeriesOptions, LegendOptions, Options
} from 'highcharts';
import generalSettings from 'js-sdk/src/jrs.configs';
import moment from 'moment';
import React from 'react';
// @ts-ignore
import i18n from '../../../../i18n/AdHocBundle.properties';
import Highcharts from '../../../api/chart/adhocToHighchartsAdapter/HighchartsTS';
import {
    DrillDownInfo, IAxisItem, ILinkToElemPair, TGroupedMeasures
} from '../../../store/types/StateTypes';
import visitMultiAxisDatasetNode from '../../../store/util/visitMultiAxisDatasetNode';
import IGenericChartVisualization, { IGenericChartVisualizationState } from './types/ChartVisualizationTypes';
import { getEvents, linkObjectToLinkData } from './util/EventUtil';
import { ITreeCategoryNode, flatCategoriesVisitor, treeCategoriesVisitor } from './util/GenericCategoriesUtil';
import {
    columnSeriesVisitor,
    rowsToArrayVisitor,
    rowsToMeasureIdsVisitor,
    getLinkObjectForAxis,
    getMeasures,
    getTooltipValue,
    getLabelToFieldMeasureMapping,
    getPath,
    getSelectedIndex,
    getIdToLabelMap
} from './util/GenericSeriesUtil';
import helpers from './util/helpers';
import { IMultiAxisDataServiceDataLayoutMemberExpansion, IMultiAxisDatasetHandle } from '../../../store/types/MultiAxisDatasetTypes';
import AdHocMessageComponent from '../../AdHocMessageComponent';

/* eslint-disable no-param-reassign, class-methods-use-this, jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid */
const extend = (target: any, source: any) => {
    Object.keys(source).forEach((key) => {
        const value = source[key];
        if (typeof value === 'object') {
            if (target[key] === undefined) {
                target[key] = {};
            }
            extend(target[key], value);
        } else {
            target[key] = value;
        }
    });
}

export default class GenericChartVisualization extends React.Component<IGenericChartVisualization, IGenericChartVisualizationState> {
    chartRef: React.RefObject<HTMLDivElement>;

    minAndMaxValues: any;

    highchartsInstance: any;

    useShadow = true

    showingSkeleton = false;

    showingHintMessage = false;

    greenLightToRenderHC = false;

    defaultFieldAggregationType = 'long';

    defaultFieldAggregationFormat = '#,##0.00';

    constructor(props: any) {
        super(props);

        this.minAndMaxValues = {
            min: null,
            max: null
        };

        this.chartRef = React.createRef();
    }

    componentDidMount() {
        this.adjustContainerFontSize();
        this.checkDataset();
    }

    componentDidUpdate() {
        this.adjustContainerFontSize();
        this.checkDataset();
    }

    getProps(): any {
        return this.props;
    }

    getDrilldownEnabled(): boolean {
        const props = this.getProps();
        const { staticProperties: { levelsStructure, drilldownEnabled } } = props;
        const hasNonMeasureRowLevels = levelsStructure.rows.find((row: any) => (row.kind === 'level'));

        return hasNonMeasureRowLevels && drilldownEnabled;
    }

    getStaticProperties(): any {
        const props = this.getProps();
        return props.staticProperties;
    }

    getHandle(): any {
        const props = this.getProps();
        return props.handle;
    }

    getLinkOptions(): any {
        const props = this.getProps();
        const { linkOptions, staticProperties: { levelsStructure } } = props;
        const drilldownEnabled = this.getDrilldownEnabled();
        let result = linkOptions;

        if (drilldownEnabled) {
            const drilldownAspect = (el: any, link: any) => {
                if (linkOptions && linkOptions.events && linkOptions.events.click) {
                    linkOptions.events.click.call(null, el, link);
                }

                const handle = this.getHandle() as IMultiAxisDatasetHandle;
                const rowSelectedIndex = this.getSelectedRowIndex();
                let step = 1;

                if (handle.drillDownTo) {
                    if (levelsStructure.rows.length > rowSelectedIndex) {
                        if (levelsStructure.rows[rowSelectedIndex].kind === 'measures') {
                            step = 2;
                        }

                        if (rowSelectedIndex + step <= levelsStructure.rows.length) {
                            handle.drillDownTo({
                                rowLevel: rowSelectedIndex + step,
                                rowFilter: levelsStructure.rows.map((row: { hierarchicalName: string }) => ({
                                    name: row.hierarchicalName,
                                    value: link[row.hierarchicalName]
                                }

                                )).slice(0, rowSelectedIndex)
                            })
                        }
                    }
                }
            }

            if (linkOptions) {
                result = {
                    ...linkOptions,
                    events: {
                        ...(linkOptions.events || {}),
                        click: drilldownAspect
                    }
                }
            } else {
                result = {
                    events: {
                        click: drilldownAspect
                    }
                }
            }
        }

        return result;
    }

    getDataset(): any {
        const props = this.getProps();
        return props.dataset;
    }

    getFormattingOptions(): any {
        const staticProperties = this.getStaticProperties();
        return staticProperties.formattingOptions || {};
    }

    getSelectedColumnIndex(): number {
        const props = this.getProps();
        const dataset = this.getDataset();
        return getSelectedIndex(props.staticProperties.onyx3ChartColumns || dataset.summary.columns.items)
    }

    getSelectedRowIndex(): number {
        const props = this.getProps();
        const dataset = this.getDataset();
        const { onyx3ChartRows, drillDown } = props.staticProperties;

        if (this.getDrilldownEnabled() && drillDown && drillDown.length) {
            return drillDown[drillDown.length - 1].rowLevel;
        }
        return getSelectedIndex(onyx3ChartRows || dataset.summary.rows.items);
    }

    // eslint-disable-next-line react/sort-comp
    recordMinMaxValues(min: number | string, max: number | string) {
        this.minAndMaxValues = {
            min: typeof (min) === 'string' ? parseFloat(min) : min,
            max: typeof (max) === 'string' ? parseFloat(max) : max
        };
    }

    getRecordedMinMaxValues() {
        return this.minAndMaxValues;
    }

    showMeasureName() {
        const formattingOptions = this.getFormattingOptions();
        if (formattingOptions.showSingleMeasuresLabels) {
            return true;
        }
        const staticProperties = this.getStaticProperties();
        const levels = staticProperties.levelsStructure;
        const measuresLevelFromColumns = levels.columns.find((level: any) => (level.kind === 'measures'));
        const measuresLevelFromRows = levels.rows.find((level: any) => (level.kind === 'measures'));
        const measuresLevel = measuresLevelFromColumns || measuresLevelFromRows;
        const amountOfMeasures :number = measuresLevel.measures.length;

        // so, we do need to include measure name if we have more than 1 measure
        return (amountOfMeasures > 1);
    }

    getContainerWidth(): number {
        if (!this.chartRef || !this.chartRef.current) {
            return 0;
        }
        return this.chartRef.current.clientWidth || 600;
    }

    getContainerHeight(): number {
        if (!this.chartRef || !this.chartRef.current) {
            return 0;
        }
        return this.chartRef.current.clientHeight || 400;
    }

    getChartSection(): ChartOptions {
        const section :any = {
            zoomType: 'xy',
            polar: false,
            style: {
                fontSize: '1em'
            },
            width: this.getContainerWidth(),
            height: this.getContainerHeight()
        };

        const formattingOptions = this.getFormattingOptions();
        if (formattingOptions.chartBackgroundColor) {
            section.backgroundColor = formattingOptions.chartBackgroundColor;
        }
        if (formattingOptions.plotBackgroundColor) {
            section.plotBackgroundColor = formattingOptions.plotBackgroundColor;
        }

        return section;
    }

    getAccessibilitySection(): any {
        return {
            enabled: false
        }
    }

    // eslint-disable-next-line class-methods-use-this
    getColorAxisSection() :any {
        // we have one chart which defines this section, and unfortunately HC applies some colors to axis
        // if we return empty object here, so this is why we return undefined.
        return undefined;
    }

    // eslint-disable-next-line class-methods-use-this
    getDefaultColorsArray() {
        return [
            '#0d233a',
            '#2f7ed8',
            '#910000',
            '#8bbc21',
            '#492970',
            '#ff7f27',
            '#ffc90e',
            '#1aadce',
            '#c42525',
            '#158349',
            '#77a1e5',
            '#c40062',
            '#dbaa00',
            '#4572a7',
            '#aa4643',
            '#89a54e',
            '#80699b',
            '#74462e',
            '#c03c03',
            '#b58c00'
        ];
    }

    getColorsToUse() :any {
        const formattingOptions = this.getFormattingOptions();
        if (formattingOptions.advancedProperties && Array.isArray(formattingOptions.advancedProperties.colors)) {
            return formattingOptions.advancedProperties.colors;
        }

        const colorsToUse = [...this.getDefaultColorsArray()];

        if (Array.isArray(formattingOptions.seriesColors)) {
            for (let i = 0; i < formattingOptions.seriesColors.length; i += 1) {
                colorsToUse[i] = formattingOptions.seriesColors[i];
            }
        }

        return colorsToUse;
    }

    getColorsSection() :any {
        return [
            ...this.getColorsToUse()
        ];
    }

    getLegendSection(): LegendOptions {
        const formattingOptions = this.getFormattingOptions();

        const legend: any = {
            enabled: true,
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
            borderWidth: formattingOptions.legendBorder ? 1 : 0,
            symbolRadius: 0,
            symbolHeight: 12,
            symbolWidth: 12,
            squareSymbol: true,
            itemMarginBottom: 5,
            itemStyle: {
                fontWeight: 'normal',
                fontSize: '1em'
            }
        };

        if (formattingOptions.legend === 'none') {
            legend.enabled = false;
        }

        if (formattingOptions.legend === 'top') {
            legend.verticalAlign = 'top';
        }

        if (formattingOptions.legend === 'left' || formattingOptions.legend === 'right') {
            legend.layout = 'vertical';
            legend.align = formattingOptions.legend;
            legend.verticalAlign = 'middle';
        }

        return legend;
    }

    getPlotOptionsSection(): any {
        const formattingOptions = this.getFormattingOptions();

        const pointFormat = '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.jrsTooltipValue}</b><br/>';

        return {
            series: {
                marker: {
                    enabled: formattingOptions.showDataPoints
                },
                tooltip: {
                    pointFormat
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                }
            }
        }
    }

    // eslint-disable-next-line class-methods-use-this
    getTooltipSection(): any {
        return {
            valueDecimals: 2,
            useHTML: true,
            style: {
                fontSize: '1em'
            },
            reverseTitle: true
        };
    }

    getCategoriesFlat(selectedRowIndex: number, labelGenerator?: Function) {
        const staticProperties = this.getStaticProperties();
        const { levelsStructure } = staticProperties;
        const dataset = this.getDataset();
        const haveRowsItems = dataset.summary.rows.items.length > 0;

        if (!haveRowsItems) {
            // there is no reason to execute the following code, we can return pre-defined value
            // if you are going to change this logic, please, check the ticket JS-57773
            return [{ name: i18n['adhoc.node.total.node'] }];
        }

        const idToLabelMap = getIdToLabelMap(staticProperties);
        const measuresInRows = levelsStructure.rows.find((level: any) => (level.kind === 'measures')) as TGroupedMeasures;

        let drilldownAwareLabelGenerator = labelGenerator;
        if (this.getDrilldownEnabled()) {
            drilldownAwareLabelGenerator = (labels: string[]) => (
                labelGenerator ? labelGenerator(labels.slice(-1)) : labels[labels.length - 1]
            );
        }

        const { categories } = visitMultiAxisDatasetNode(dataset.data.rows, flatCategoriesVisitor, {
            categories: [],
            stack: [],
            selectedRowIndex,
            levels: levelsStructure.rows as IAxisItem[],
            measures: measuresInRows ? measuresInRows.measures : [],
            labelGenerator: drilldownAwareLabelGenerator,
            idToLabelMap,
            includeMeasureName: this.showMeasureName()
        });

        if (!categories || categories.length === 0) {
            return [{ name: i18n['adhoc.node.total.node'] }];
        }

        return categories;
    }

    getCategoriesTree(selectedRowIndex: number, supplyWithBlankCategory :boolean = true) {
        const dataset = this.getDataset();
        const staticProperties = this.getStaticProperties();
        const { levelsStructure } = staticProperties;
        const haveRowsItems = dataset.summary.rows.items.length > 0;

        if (!haveRowsItems) {
            // there is no reason to execute the following code, we can return pre-defined value
            // if you are going to change this logic, please, check the ticket JS-57773
            if (supplyWithBlankCategory) {
                return [{ name: i18n['adhoc.node.total.node'] }];
            }
            return [];
        }

        const idToLabelMap = getIdToLabelMap(staticProperties);
        const measuresInRows = levelsStructure.rows.find((level: any) => (level.kind === 'measures'));

        // eslint-disable-next-line prefer-destructuring
        let { categories } = visitMultiAxisDatasetNode(dataset.data.rows, treeCategoriesVisitor, {
            stack: [],
            selectedRowIndex,
            levels: levelsStructure.rows,
            measureLabels: measuresInRows ? measuresInRows.measures.map((measure: any) => (measure.label)) : [],
            idToLabelMap,
            includeMeasureName: this.showMeasureName()
        });

        if (!categories || categories.length === 0) {
            if (supplyWithBlankCategory) {
                return [{ name: i18n['adhoc.node.total.node'] }];
            }
        }

        if (this.getDrilldownEnabled() && categories && categories.length) {
            categories = (function categoriesFlattener(cats): (string | ITreeCategoryNode)[] {

                if (cats[0] && typeof cats[0] !== 'string' && cats[0].categories && cats[0].categories.length) {
                    return categoriesFlattener(cats[0].categories);
                }

                return cats;
            }(categories));
        }

        return categories;
    }

    // eslint-disable-next-line react/sort-comp
    generateXAxisLabelsSection(): any {
        const formattingOptions = this.getFormattingOptions();

        const labelsOptions :any = {
            allowOverlap: true,
            maxStaggerLines: 1,
            rotation: formattingOptions.xAxisRotation,
            step: formattingOptions.xAxisStep,
            style: {
                fontSize: '1em'
            }
        };

        // JS-48574: the very top x-axis may have different levels, we call them categories.
        // the very first (top) level is rendered by Highcharts native code, the rest levels
        // are rendered with grouped-categories module, and we want to avoid rotation on these levels.
        // This code implements it: it adds 'rotation' property to each grouped level.
        let fieldsAmount :number = 0;
        const dataset = this.getDataset();
        dataset.summary.rows.items.forEach((item :any) => {
            if (item.measuresLevel === false) fieldsAmount += 1;
        });
        dataset.summary.columns.items.forEach((item :any) => {
            if (item.measuresLevel === false) fieldsAmount += 1;
        });
        if (fieldsAmount > 1) {
            labelsOptions.groupedOptions = [];
            for (let i = 1; i < fieldsAmount; i += 1) {
                labelsOptions.groupedOptions.push({
                    rotation: 0
                });
            }
        }

        // add some shift for labels if they are rotated vertically. Fixes bug #35881
        if (labelsOptions.rotation === 90) {
            // @ts-ignore
            labelsOptions.x = -4;
        } else if (labelsOptions.rotation === 270) {
            // @ts-ignore
            labelsOptions.x = 4;
        }

        return labelsOptions;
    }

    // eslint-disable-next-line class-methods-use-this
    getXAxisTitleSection(): any {
        return {
            text: ''
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getXAxisSection(selectedRowIndex: number): AxisOptions {
        const formattingOptions = this.getFormattingOptions();
        const offset = Math.abs(formattingOptions.xAxisRotation) > 60 ? 5 : 0;

        return {
            title: {
                text: ''
            },
            labels: this.generateXAxisLabelsSection(),
            offset
        };
    }

    getYAxisSection(): any {
        const staticProperties = this.getStaticProperties();
        const formattingOptions = this.getFormattingOptions();
        const levelsLayout = staticProperties.levelsStructure;

        const { measures } = levelsLayout.rows.concat(levelsLayout.columns)
            .find((level: any) => (level.kind === 'measures')) as TGroupedMeasures;

        let text = ' ';
        if (formattingOptions.showMeasureOnValueAxis && measures.length === 1) {
            text = helpers.escapeTitle((measures[0] as IAxisItem).label)
        }

        return {
            title: { text },
            labels: {
                allowOverlap: true,
                rotation: formattingOptions.yAxisRotation,
                step: formattingOptions.yAxisStep,
                style: {
                    fontSize: '1em'
                }
            }
        };
    }

    getColumnSeries() :any[] {
        const dataset = this.getDataset();
        const staticProperties = this.getStaticProperties();
        const selectedColIndex = this.getSelectedColumnIndex();

        let columnSeries :any[] = [];

        const result = visitMultiAxisDatasetNode(dataset.data.columns, columnSeriesVisitor, {
            colors: this.getColorsToUse(),
            includeMeasureName: this.showMeasureName(),
            selectedColIndex: this.getSelectedColumnIndex(),
            levelsStructure: staticProperties.levelsStructure,
            skipTotals: dataset.summary.columns.items.length === selectedColIndex
        }).array;

        if (result) {
            columnSeries = result;
        }

        return columnSeries;
    }

    getRowIndexes() {
        const dataset = this.getDataset();
        const selectedRowIndex = this.getSelectedRowIndex();

        return visitMultiAxisDatasetNode(dataset.data.rows, rowsToArrayVisitor, {
            array: [],
            indexes: [],
            stack: [],
            selectedRowIndex: this.getSelectedRowIndex(),
            skipTotals: dataset.summary.rows.items.length === selectedRowIndex
        }).indexes;
    }

    getMeasureIdsFromRows() {
        const dataset = this.getDataset();

        return visitMultiAxisDatasetNode(dataset.data.rows, rowsToMeasureIdsVisitor, {
            measureIds: [],
            selectedRowIndex: this.getSelectedRowIndex()
        }).measureIds;
    }

    getSeriesSection(selectedColIndex: number, selectedRowIndex: number): IndividualSeriesOptions[] {
        const dataset = this.getDataset();
        const linkOptions = this.getLinkOptions();
        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);
        const measuresMapping = getLabelToFieldMeasureMapping(staticProperties.levelsStructure);
        const measuresFromRows = staticProperties.levelsStructure.rows.find((level: any) => (level.kind === 'measures'));
        const getTooltipProps = {
            measures,
            defaultFieldAggregationType: this.defaultFieldAggregationType,
            defaultFieldAggregationFormat: this.defaultFieldAggregationFormat
        };

        const columnSeries = this.getColumnSeries();
        const rowsIndexesArray = this.getRowIndexes();
        let measureIdsFromRows :any = [];
        if (measuresFromRows) {
            measureIdsFromRows = this.getMeasureIdsFromRows();
        }

        let min: any = null,
            max: any = null;

        const series = columnSeries.map((seriesObj: any) => {
            const {
                series: serie,
                colIndex,
                measureId: measureIdFromColumn
            } = seriesObj;

            serie.data = rowsIndexesArray.reduce((memo: any, rowIndex :number, itemIndex :number) => {
                const measureIdFromRow = measureIdsFromRows[itemIndex];

                const aggregationValue = dataset.data.aggregations[rowIndex][colIndex];
                const value = typeof aggregationValue === 'string' ? parseFloat(aggregationValue) : aggregationValue;

                if (min === null) {
                    min = value;
                    max = value;
                } else {
                    min = Math.min(value, min);
                    max = Math.max(value, max);
                }

                const measureId = measureIdFromColumn || measureIdFromRow;
                const tooltipValue = getTooltipValue(getTooltipProps, aggregationValue, null, measureId);

                const item = {
                    y: value,
                    // this variable 'jrsTooltipValue' contains the text we want to show
                    // on the tooltip, and this is the way how we pass it to the function which renders the tooltip
                    jrsTooltipValue: tooltipValue
                };
                if (linkOptions) {
                    const rowPath = getPath(rowIndex, dataset.data.rows);
                    const linkOutputParamsCols = getLinkObjectForAxis(selectedColIndex, dataset.summary.columns.items, measuresMapping, serie.path);
                    const linkOutputParamsRows = getLinkObjectForAxis(selectedRowIndex, dataset.summary.rows.items, measuresMapping, rowPath);

                    const linkOutputMeasures = linkOutputParamsRows.measures.length ? linkOutputParamsRows.measures : linkOutputParamsCols.measures;
                    const values = linkOutputParamsCols.values.concat(linkOutputParamsRows.values);

                    // @ts-ignore
                    item.linkOutputParams = {
                        measures: linkOutputMeasures,
                        values
                    };
                }
                memo.push(item);

                return memo;
            }, [] as Array<{ y: any }>);

            if (linkOptions) {
                serie.cursor = 'pointer';
                serie.point = {
                    events: getEvents(linkOptions),
                }
            }

            delete serie.path;
            return serie;
        });

        this.recordMinMaxValues(min, max);

        return series;
    }

    getTimeSection() {
        // Please, read carefully text below before changing time options.

        // When HC works with time we need to maintain control over the timezone offset calculations.
        // Highcharts.Time allows us to do this by passing 3 parameters:
        //      'getTimezoneOffset'
        //      'timezone'
        //      'timezoneOffset'.
        // Parameter 'timezone' will be passed to momentJS library by HC.
        // Parameter 'timezoneOffset' will be used directly to calculate offset (`timezoneOffset * 60000`).
        // Parameter 'getTimezoneOffset' is function which will be called each time by HC.

        // If you take a look at timezoneOffsetFunction inside highcharts.js file you'll notice
        // that it checks for 'useUTC' variable, and if it set to 'false' HC will try to get
        // browser's timezone offset by calling next code:
        //      new Date(timestamp).getTimezoneOffset() * 60000;
        // It works (gives browser's timezone offset) because Date() always converts incoming time into
        // browser's timezone. But this is not what we want (user may select some other timezone),
        // and this is why you'll see how 'useUTC' parameter set to 'true'.
        // (HC should call this parameter 'dontUseBrowsersTimezone' instead of 'useUTC')

        // If you ask "Why not to use 'timezoneOffset' parameter if we have a static zone value?"
        // I'd say because timezone offsets were different for different period of time for timezones.
        // For instance, DST changes timezone offset, and some countries changed offset for their zones
        // back and forth in history.

        // If you ask "Why not to use 'timezone' parameter?"
        // I'd say good question, but in this case it would be exactly the same as we have, and since
        // we like to maintain control I prefer to define calculating offset in our code.

        // So, we define 'getTimezoneOffset' to let HC take our timezone off value.

        // Please, read carefully text above before changing time options.
        return {
            useUTC: true,
            getTimezoneOffset: (timestamp: Date) => {
                return -1 * (moment as any).tz(timestamp, generalSettings.userTimezone).utcOffset();
            }
        };
    }

    getHighchartsOptions() {
        const selectedColIndex = this.getSelectedColumnIndex();
        const selectedRowIndex = this.getSelectedRowIndex();

        let highchartsOptions: Options = {
            chart: this.getChartSection(),
            accessibility: this.getAccessibilitySection(),
            credits: {
                enabled: false
            },
            colors: this.getColorsSection(),
            legend: this.getLegendSection(),
            plotOptions: this.getPlotOptionsSection(),
            series: this.getSeriesSection(selectedColIndex, selectedRowIndex),
            time: this.getTimeSection(),
            title: {
                text: null
            },
            tooltip: this.getTooltipSection(),
            xAxis: this.getXAxisSection(selectedRowIndex),
            yAxis: this.getYAxisSection()
        };

        // Here we define some sections which are not needed almost by any chart,
        // so if some chart type would redefine these methods and return some valuable result,
        // it would be added to final config.
        const colorAxis = this.getColorAxisSection();
        if (colorAxis) {
            highchartsOptions.colorAxis = colorAxis;
        }

        highchartsOptions = this.finalOptionAdjustment(highchartsOptions);

        // console.log('highchartsOptions:', JSON.parse(JSON.stringify(highchartsOptions)));

        return highchartsOptions;
    }

    adjustContainerFontSize() {
        if (!this.chartRef.current) {
            return;
        }

        const formattingOptions = this.getFormattingOptions();
        if (!formattingOptions.autoScaleFonts) {
            return;
        }

        // The default font size is defined in file '_chart.scss' file within the selector '.jr-mChart'.
        // Since that number is not going to be changed quite often, we may simplify the code and avoid
        // detecting that number right here, and use a constant instead.
        const defaultFontSize = 12; // in pixels
        const containerWidth = this.chartRef.current.clientWidth;

        let scale = 1;
        if (containerWidth <= 100) {
            scale = 0.4;
        } else if (containerWidth > 100 && containerWidth <= 200) {
            scale = 0.5;
        } else if (containerWidth > 200 && containerWidth <= 300) {
            scale = 0.6;
        } else if (containerWidth > 300 && containerWidth <= 400) {
            scale = 0.7;
        } else if (containerWidth > 400 && containerWidth <= 500) {
            scale = 0.8;
        } else if (containerWidth > 500 && containerWidth <= 600) {
            scale = 0.9;
        }

        const newFontSize = defaultFontSize * scale;

        this.chartRef.current.style.fontSize = `${newFontSize}px`;
    }

    applyShadowSettings(highchartsOptions: any) :any {
        const types = [];
        if (highchartsOptions.chart.type) {
            types.push(highchartsOptions.chart.type);
        } else {
            highchartsOptions.series.forEach((serie :any) => {
                if (serie.type) {
                    types.push(serie.type);
                }
            });
        }
        if (types.length === 0) {
            types.push('line');
        }

        const { plotOptions } = highchartsOptions;
        types.forEach((type) => {
            if (!plotOptions[type]) {
                plotOptions[type] = {};
            }
            plotOptions[type].shadow = this.useShadow;
        });
    }

    applyAdvancedProperties(highchartsOptions :any) {
        const formattingOptions = this.getFormattingOptions();

        if (!formattingOptions) {
            return;
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const name of Object.keys(formattingOptions.advancedProperties)) {
            const value :any = formattingOptions.advancedProperties[name];
            this.applyAdvancedProperty(highchartsOptions, name, value);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    applyAdvancedProperty(highchartsOptions :any, name :string, value :any) {

        const partials = name.split('.');
        const last = partials.length - 1;
        let tmp :any = {};
        const result :any = tmp;

        partials.forEach((part, index) => {
            if (index === last) {
                tmp[part] = value;
            } else {
                tmp[part] = {};
                tmp = tmp[part];
            }
        });

        const topLevelKey = partials[0];
        if (Array.isArray(highchartsOptions[topLevelKey])) {

            // Unfortunately, there is no official explanation how this code should work,
            // so we have to maintain the same logic which was established by initial code
            // + fix some issues. Otherwise it would lead to regressions.

            if (typeof highchartsOptions[topLevelKey][0] === 'object'
                && typeof result[topLevelKey] === 'object') {

                // in this case the code extends each object in the array highchartsOptions[topLevelKey] with some
                // object provided by the user.
                // Of course, we assume that the highchartsOptions[topLevelKey] has similar items,
                // so by checking just the first element we assert all the rest have the same kind

                highchartsOptions[topLevelKey].forEach((item :any) => {
                    extend(item, result[topLevelKey]);
                });
            } else {

                // this can be a case when user defines colors:
                // colors: ["blue", "green", "magenta", "purple"]
                // in such cases we just need to copy them, because user
                // specifies the content of the array

                // eslint-disable-next-line no-param-reassign
                highchartsOptions[topLevelKey] = result[topLevelKey];
            }

        } else {
            extend(highchartsOptions, result);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    finalOptionAdjustment(highchartsOptions: any): any {

        let newOptions = helpers.setTurboThreshold(highchartsOptions);

        this.applyShadowSettings(newOptions);

        newOptions = helpers.correctEmptySeriesNames(newOptions);

        if (newOptions.xAxis && newOptions.xAxis.categories && newOptions.xAxis.categories.length === 0) {
            // if we don't specify anything in the categories list, the HC will render category as '0',
            // which is not what we need. An empty string works better.
            newOptions.xAxis.categories = [{ name: '' }];
        }

        if (newOptions?.plotOptions?.series?.tooltip?.pointFormat) {
            let seriesNameIsFulfilled = true;
            if (Array.isArray(newOptions.series)) {
                for (let i = 0; i < newOptions.series.length; i += 1) {
                    const serie = newOptions.series[i];
                    seriesNameIsFulfilled = Boolean(serie.name && serie.name.toString().trim() !== '');
                    if (seriesNameIsFulfilled) {
                        break;
                    }
                }
            } else {
                seriesNameIsFulfilled = Boolean(newOptions.series.name && newOptions.series.name.toString().trim() !== '');
            }
            if (!seriesNameIsFulfilled) {
                newOptions.plotOptions.series.tooltip.pointFormat = '<span style="color:{point.color}">\u25CF</span> <b>{point.jrsTooltipValue}</b><br/>';
            }
        }

        this.applyAdvancedProperties(newOptions);

        return newOptions;
    }

    checkDataset() {
        if (this.greenLightToRenderHC) {
            this.renderChartByHighchart();
        } else if (this.highchartsInstance) {
            this.highchartsInstance.destroy();
            this.highchartsInstance = null;
        }
    }

    checkAndExecuteBeforeRenderCallback(highchartsOptions :any) {
        const linkOptions = this.getLinkOptions();
        if (!linkOptions) return;
        if (typeof linkOptions.beforeRender !== 'function') return;
        if (!highchartsOptions.series) return;

        const data: ILinkToElemPair[] = [];

        highchartsOptions.series.forEach((series :any) => {
            if (!series.data) return;

            series.data.forEach((point: any) => {
                if (!point.linkOutputParams) return;

                data.push({
                    element: null,
                    data: linkObjectToLinkData(point.linkOutputParams)
                })
            });
        });

        linkOptions.beforeRender(data);
    }

    renderChartByHighchart() {
        const highchartsOptions = this.getHighchartsOptions();

        this.checkAndExecuteBeforeRenderCallback(highchartsOptions);

        if (this.highchartsInstance) {
            this.highchartsInstance.destroy();
        }

        this.highchartsInstance = new Highcharts.Chart(this.chartRef.current!, highchartsOptions);
    }

    resetForDrilldown() {
        const { staticProperties: { levelsStructure, drillDown } } = this.getProps();
        const firstRowIsMeasure = levelsStructure.rows[0] && levelsStructure.rows[0].kind === 'measures';
        const resetToRowLevel = firstRowIsMeasure ? 2 : 1;
        const currentDrilldownLevel = drillDown[drillDown.length - 1];

        if (!currentDrilldownLevel || currentDrilldownLevel.rowLevel !== resetToRowLevel) {
            this.getHandle().drillDownTo({
                rowLevel: resetToRowLevel,
                rowFilter: []
            });
        }
    }

    render() {
        const dataset = this.getDataset();
        const memberExpansions = this.hasMemberExpansions();
        const { staticProperties: { drillDown } } = this.getProps();
        const drilldownEnabled = this.getDrilldownEnabled();

        const resetForDrilldown = drilldownEnabled && !drillDown.length;

        let content,
            drillDownBreadcrumb = null;

        this.showingSkeleton = !dataset.summary.complete || memberExpansions || resetForDrilldown;
        this.showingHintMessage = !this.showingSkeleton && dataset.summary.empty;
        this.greenLightToRenderHC = !this.showingSkeleton && !this.showingHintMessage;

        if (this.showingSkeleton) {
            if (memberExpansions) {
                this.removeMemberExpansions();
            } else if (resetForDrilldown) {
                setTimeout(() => {
                    this.resetForDrilldown();
                });
            }

            /* eslint-disable react/jsx-closing-tag-location */
            content = (
                <div
                    className="jr-mSkeletonContainer jr"
                    style={{
                        width: '100%',
                        height: '100%'
                    }}
                >
                    <div className="jr-mSkeleton jr-mSkeletonChart jr">
                        <div className="jr-mSkeleton-bars jr">
                            <div className="jr-mSkeleton-bars-bar jr" />
                            <div className="jr-mSkeleton-bars-bar jr" />
                            <div className="jr-mSkeleton-bars-bar jr" />
                            <div className="jr-mSkeleton-bars-bar jr" />
                            <div className="jr-mSkeleton-bars-bar jr" />
                        </div>
                        <div className="jr-mSkeleton-grid jr">
                            <div className="jr-mSkeleton-grid-line jr" />
                            <div className="jr-mSkeleton-grid-line jr" />
                            <div className="jr-mSkeleton-grid-line jr" />
                            <div className="jr-mSkeleton-grid-line jr" />
                            <div className="jr-mSkeleton-grid-line jr" />
                        </div>
                    </div>
                </div>
            );
        } else if (this.showingHintMessage) {
            content = (
                <AdHocMessageComponent />
            )
        } else if (drilldownEnabled) {
            content = (<div ref={this.chartRef} className="jr-mChart jr" style={{ height: 'calc(100% - 48px)' }} />);

            drillDownBreadcrumb = (
                <div className="jr-mBreadcrumbs jr">
                    <a
                        className="jr-mBreadcrumbs-crumb jr"
                        key={0}
                        onClick={() => {
                            this.resetForDrilldown();
                        }}
                    >{i18n['adhoc.node.total.node']}</a>

                    {drillDown.slice(1, drillDown.length - 1).map((info: DrillDownInfo, index: number) => (
                        <a
                            className="jr-mBreadcrumbs-crumb jr"
                            key={info.rowLevel}
                            onClick={() => {
                                if (drillDown[index + 2]) {
                                    this.getHandle().drillDownTo(drillDown[index + 2]);
                                }

                            }}
                        >{info.rowFilter.length ? info.rowFilter.slice(-1)[0].value : 'no value'}</a>
                    ))}

                    {drillDown[drillDown.length - 1].rowFilter.length
                        ? <a className="jr-mBreadcrumbs-crumb jr" key={drillDown.length - 1}>{drillDown[drillDown.length - 1].rowFilter.slice(-1)[0].value}</a> : null}

                </div>
            )
        } else {
            content = (<div ref={this.chartRef} className="jr-mChart jr" />);
        }

        return (
            <div className="jr-mChartContainer jr-uHeight-100pr jr">
                {drillDownBreadcrumb}
                {content}
            </div>
        );
    }

    private hasMemberExpansions() {
        const dataset = this.getDataset();
        return Boolean(dataset.summary.rows.expansions.length || dataset.summary.columns.expansions.length);
    }

    private removeMemberExpansions() {
        const handle = this.getHandle();
        const dataset = this.getDataset();

        dataset.summary.rows.expansions.forEach((rowexp: IMultiAxisDataServiceDataLayoutMemberExpansion) => {
            handle.updateMemberExpansion({
                path: rowexp.path,
                expanded: !rowexp.expanded
            }, false, null);
        });

        dataset.summary.columns.expansions.forEach((colexp: IMultiAxisDataServiceDataLayoutMemberExpansion) => {
            handle.updateMemberExpansion({
                path: colexp.path,
                expanded: !colexp.expanded
            }, true, null);
        });
    }
}
